import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Camera, ImagePlus, Trash2, UserRound, X } from "lucide-react";
import {
  addOwnProfilePhoto,
  deleteOwnProfilePhoto,
  getUserProfile,
  reorderOwnProfilePhotos,
  updateOwnAvatar,
  updateOwnProfile,
} from "../api";
import { compressProfileImage } from "../imageCompression";
import type { User, UserProfile } from "../types";
import { UserAvatar } from "./UserAvatar";

type Props = Readonly<{
  onClose: () => void;
  onUpdated: (user: User) => void;
  userId: string;
}>;

export function ProfileModal({ onClose, onUpdated, userId }: Props) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [avatarColor, setAvatarColor] = useState("#5b8cff");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const avatarInput = useRef<HTMLInputElement>(null);
  const photosInput = useRef<HTMLInputElement>(null);

  const load = async () => {
    const next = await getUserProfile(userId);
    setProfile(next);
    setDisplayName(next.user.displayName);
    setBio(next.bio);
    setAvatarColor(next.user.avatarColor);
    onUpdated(next.user);
  };

  useEffect(() => {
    let active = true;
    void getUserProfile(userId).then((next) => {
      if (!active) return;
      setProfile(next);
      setDisplayName(next.user.displayName);
      setBio(next.bio);
      setAvatarColor(next.user.avatarColor);
    }).catch((caught) => active && setError(caught instanceof Error ? caught.message : "Не удалось открыть профиль"));
    return () => { active = false; };
  }, [userId]);

  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Не удалось обновить профиль";
      await load().catch(() => undefined);
      setError(message);
    } finally {
      setBusy(false);
      if (avatarInput.current) avatarInput.current.value = "";
      if (photosInput.current) photosInput.current.value = "";
    }
  };

  const chooseAvatar = (file?: File) => {
    if (!file) return;
    void run(async () => updateOwnAvatar(await compressProfileImage(file, "avatar")));
  };

  const choosePhotos = (files: FileList | null) => {
    if (!files?.length || !profile) return;
    const available = 5 - profile.photos.length;
    if (files.length > available) {
      setError(`Можно добавить ещё ${available} фото.`);
      return;
    }
    void run(async () => {
      for (const file of Array.from(files)) await addOwnProfilePhoto(await compressProfileImage(file, "photo"));
    });
  };

  const movePhoto = (index: number, offset: -1 | 1) => {
    if (!profile) return;
    const next = [...profile.photos];
    const target = index + offset;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    void run(() => reorderOwnProfilePhotos(next.map((item) => item.id)));
  };

  return (
    <div className="modal-backdrop profile-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }} role="presentation">
      <section aria-modal="true" className="modal-card profile-modal" role="dialog">
        <header>
          <div><p className="eyebrow">ПРОФИЛЬ</p><h2>{profile?.own ? "Ваш профиль" : "Профиль пользователя"}</h2></div>
          <button aria-label="Закрыть профиль" className="icon-button" onClick={onClose} type="button"><X /></button>
        </header>
        {!profile && !error && <p className="manager-empty">Загружаем профиль…</p>}
        {error && <div className="inline-error" role="alert">{error}</div>}
        {profile && <>
          <section className="profile-hero">
            <div className="profile-avatar-wrap">
              <UserAvatar className="avatar profile-avatar" user={{ ...profile.user, avatarColor }} />
              {profile.own && <button aria-label="Изменить аватар" disabled={busy} onClick={() => avatarInput.current?.click()} type="button"><Camera /></button>}
            </div>
            <div className="profile-identity"><h3>{profile.user.displayName}</h3><span>@{profile.user.username}</span><small>В JTUC Live с {new Date(profile.createdAt).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" })}</small></div>
          </section>
          {profile.own ? <form className="profile-form" onSubmit={(event) => { event.preventDefault(); void run(() => updateOwnProfile({ displayName, bio, avatarColor })); }}>
            <label><span>Имя для собеседников</span><input disabled={busy} maxLength={80} onChange={(event) => setDisplayName(event.target.value)} required value={displayName} /></label>
            <label><span>О себе <small>{bio.length}/500</small></span><textarea disabled={busy} maxLength={500} onChange={(event) => setBio(event.target.value)} placeholder="Несколько слов о себе" rows={4} value={bio} /></label>
            <label className="profile-color"><span>Цвет без фотографии</span><input aria-label="Цвет аватара" disabled={busy} onChange={(event) => setAvatarColor(event.target.value)} type="color" value={avatarColor} /></label>
            <button className="primary-button compact" disabled={busy || !displayName.trim()} type="submit">{busy ? "Сохраняем…" : "Сохранить профиль"}</button>
          </form> : <p className={`profile-bio ${profile.bio ? "" : "empty"}`}>{profile.bio || "Пользователь пока ничего о себе не написал."}</p>}
          <section className="profile-gallery-section">
            <header><span><b>Фотографии</b><small>{profile.photos.length} из 5</small></span>{profile.own && profile.photos.length < 5 && <button className="secondary-button" disabled={busy} onClick={() => photosInput.current?.click()} type="button"><ImagePlus />Добавить</button>}</header>
            {profile.photos.length === 0 ? <div className="profile-gallery-empty"><UserRound /><span>Фотографий пока нет</span></div> : <div className="profile-gallery">{profile.photos.map((photo, index) => <article key={photo.id}>
              <button className="profile-photo-open" onClick={() => setSelectedPhoto(photo.url)} type="button"><img alt={`Фото профиля ${index + 1}`} decoding="async" loading="lazy" src={photo.url} /></button>
              {profile.own && <span className="profile-photo-actions"><button aria-label="Переместить выше" disabled={busy || index === 0} onClick={() => movePhoto(index, -1)} type="button"><ArrowUp /></button><button aria-label="Переместить ниже" disabled={busy || index === profile.photos.length - 1} onClick={() => movePhoto(index, 1)} type="button"><ArrowDown /></button><button aria-label="Удалить фотографию" className="danger" disabled={busy} onClick={() => void run(() => deleteOwnProfilePhoto(photo.id))} type="button"><Trash2 /></button></span>}
            </article>)}</div>}
          </section>
          {profile.own && <div className="profile-avatar-actions"><input accept="image/avif,image/heic,image/heif,image/jpeg,image/png,image/webp" hidden onChange={(event) => chooseAvatar(event.target.files?.[0])} ref={avatarInput} type="file" /><input accept="image/avif,image/heic,image/heif,image/jpeg,image/png,image/webp" hidden multiple onChange={(event) => choosePhotos(event.target.files)} ref={photosInput} type="file" />{profile.avatar && <button className="text-button danger" disabled={busy} onClick={() => void run(() => updateOwnAvatar(null))} type="button"><Trash2 />Удалить аватар</button>}<small>Изображения уменьшаются, очищаются от метаданных и сохраняются в WebP. Фото профиля доступны авторизованным пользователям и не относятся к E2EE-переписке.</small></div>}
        </>}
      </section>
      {selectedPhoto && <button aria-label="Закрыть фотографию" className="profile-lightbox" onClick={() => setSelectedPhoto(null)} type="button"><img alt="Фотография профиля" src={selectedPhoto} /><X /></button>}
    </div>
  );
}
